import * as React from "react";
import { useRef, useState } from "react";
import List from "./List";
import DataItemSkeleton from "./DataItemSkeleton";
import ListItem from "./ListItem";
import Box from "@sproutsocial/seeds-react-box";
import Container from "./styles";
import type { TypeDataListProps } from "./DataListTypes";
import styled from "styled-components";
import { Pagination } from "@sproutsocial/seeds-react-pagination";

const HeaderContent = styled(Box)`
  width: 100%;
  transition: transform 0.4s ease-in-out;
`;

const DataList = <T,>({
  as = "ul",
  data,
  renderItem,
  isLoading,
  LoadingComponent = DataItemSkeleton,
  listItemProps = {},
  estimateItemSize = 50,
  Header,
  paginationConfig,
  currentPageIndex: controlledPageIndex,
  pageSize: controlledPageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  ...rest
}: TypeDataListProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadingItems = [0, 1, 2, 3, 4];

  // Internal state for uncontrolled pagination
  const [internalPageIndex, setInternalPageIndex] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(
    paginationConfig?.initialPageSize ?? 10
  );

  // Use controlled props if provided, otherwise use internal state
  const currentPageIndex = controlledPageIndex ?? internalPageIndex;
  const pageSize = controlledPageSize ?? internalPageSize;

  // Use provided callbacks or internal state setters
  const handlePageChange = onPageChange ?? setInternalPageIndex;
  const handlePageSizeChange = onPageSizeChange ?? setInternalPageSize;

  // Calculate paginated data if pagination is enabled
  const displayData = React.useMemo(() => {
    if (!paginationConfig) {
      return data;
    }
    const startIndex = currentPageIndex * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, paginationConfig, currentPageIndex, pageSize]);

  const totalRows = totalItems !== undefined ? totalItems : data.length;

  // Early return for loading state
  if (isLoading) {
    return (
      <>
        {Header && (
          <HeaderContent
            style={{
              position: "sticky",
              top: 0,
              left: 0,
              width: "100%",
              zIndex: 1,
            }}
            id="header"
          >
            {Header}
          </HeaderContent>
        )}
        <List as={as} {...rest}>
          {loadingItems.map((index) => (
            <ListItem key={index} {...listItemProps}>
              <LoadingComponent />
            </ListItem>
          ))}
        </List>
      </>
    );
  }

  return (
    <>
      {Header && (
        <HeaderContent
          style={{
            position: "sticky",
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 1,
          }}
          id="header"
        >
          {Header}
        </HeaderContent>
      )}
      <List as={as} {...rest}>
        {displayData.map((item, index) => (
          <ListItem key={index} {...listItemProps}>
            {renderItem(item, index)}
          </ListItem>
        ))}
      </List>
      {paginationConfig && (
        <Pagination
          paginationConfig={paginationConfig}
          currentPageIndex={currentPageIndex}
          pageSize={pageSize}
          totalRows={totalRows}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
    </>
  );
};

DataList.displayName = "DataList";

export default DataList;
